import { useState } from 'react';
import { useIntl } from 'react-intl';

import { useToast } from '~/eds/src/components/hooks/useToast';
import { UserAction } from '~/eds/src/types/UserAction';
import { api } from '~/redux/api/createApi';
import { openAdminConsentPopup } from '~/redux/api/methods/sharepoint-embedded/connectApp';
import { SPEConfiguration } from '~/redux/api/methods/spe';

export const useGetAccessConditions = () => {
  const intl = useIntl();
  const earlyAccess: UserAction = {
    text: intl.formatMessage({
      id: 'Integrations.earlyAccess',
      defaultMessage: 'Early Access',
    }),
    mode: 'chip',
    status: 'active',
    tooltip: intl.formatMessage({
      id: 'Integrations.earlyAccessTooltip',
      defaultMessage: 'Reach out to your CSM for more information',
    }),
  };

  const comingSoon: UserAction = {
    text: intl.formatMessage({
      id: 'Integrations.comingSoon',
      defaultMessage: 'Coming Soon',
    }),
    mode: 'chip',
    status: 'inactive',
  };

  const futureDevelopment: UserAction = {
    text: intl.formatMessage({
      id: 'Integrations.futureDevelopment',
      defaultMessage: 'Future Development',
    }),
    mode: 'chip',
    status: 'inactive',
  };

  const configured: UserAction = {
    text: intl.formatMessage({
      id: 'Integrations.configured',
      defaultMessage: 'Configured',
    }),
    mode: 'chip',
    status: 'success',
  };

  const reconnect: UserAction = {
    text: intl.formatMessage({
      id: 'Integrations.reconnect',
      defaultMessage: 'Reconnect',
    }),
    mode: 'chip',
    icon: 'rerun',
    iconPosition: 'left',
    status: 'danger',
  };

  const configure: UserAction = {
    text: intl.formatMessage({
      id: 'Integrations.configure',
      defaultMessage: 'Configure',
    }),
    mode: 'chip',
    status: 'active',
    icon: 'plus-circle',
    iconPosition: 'left',
  };

  const syncing: UserAction = {
    text: intl.formatMessage({
      id: 'Integrations.syncing',
      defaultMessage: 'Syncing...',
    }),
    mode: 'chip',
    status: 'info',
    icon: 'loading',
    iconPosition: 'left',
    disabled: true,
    tooltip: intl.formatMessage({
      id: 'Integrations.syncingDisabledTooltip',
      defaultMessage: 'Sync is in progress. Please wait before making changes.',
      description:
        'Tooltip message shown when sync is in progress and action button is disabled',
    }),
  };

  const remove: UserAction = {
    text: intl.formatMessage({
      id: 'Integrations.remove',
      defaultMessage: 'Remove',
    }),
    mode: 'chip',
    icon: 'trash',
    status: 'danger',
    iconPosition: 'left',
  };

  return {
    earlyAccess,
    comingSoon,
    futureDevelopment,
    configured,
    reconnect,
    configure,
    syncing,
    remove,
  };
};

export const useSharepointConfiguration = () => {
  const intl = useIntl();

  const { data: integrationConfigurationStatus } =
    api.endpoints.getSharepointConfigurationStatus.useQuery(undefined);

  const [createSharepointConfiguration, createResult] =
    api.endpoints.createSharepointConfiguration.useMutation();

  const saveConfiguration = (
    microsoftTenantId: string,
    sharePointSiteUrl: string,
    id?: string,
  ) => {
    createSharepointConfiguration({
      tenantId: microsoftTenantId,
      sharepointUrl: sharePointSiteUrl,
      id,
      rtkQueryOptions: {
        showSuccessToast: intl.formatMessage({
          id: 'DocumentEditingDetailCard.saveConfigurationSuccess',
          defaultMessage: 'Configuration saved successfully',
          description: 'Success message when configuration is saved',
        }),
        showErrorToast: intl.formatMessage({
          id: 'DocumentEditingDetailCard.saveConfigurationError',
          defaultMessage: 'Failed to save configuration',
          description: 'Error message when configuration is saved fails',
        }),
      },
    });
  };

  return {
    integrationConfigurationStatus:
      integrationConfigurationStatus as SPEConfiguration,
    saveConfiguration,
    isLoading: createResult.isLoading,
  };
};

export const useAdminConsent = (options?: {
  onSuccess?: (tenantId: string) => void;
}) => {
  const { onSuccess } = options ?? {};
  const intl = useIntl();
  const { toast } = useToast();
  const [isConsentLoading, setIsConsentLoading] = useState(false);

  const handleGrantAdminConsent = async () => {
    setIsConsentLoading(true);
    try {
      const result = await openAdminConsentPopup();
      if (result.success && result.tenantId) {
        toast({
          status: 'success',
          message: intl.formatMessage({
            id: 'MicrosoftWordIntegrationCard.consentSuccess',
            defaultMessage: 'Admin consent granted successfully',
            description: 'Success message when admin consent is granted',
          }),
        });
        onSuccess?.(result.tenantId);
      } else {
        toast({
          status: 'danger',
          message: intl.formatMessage({
            id: 'MicrosoftWordIntegrationCard.consentError',
            defaultMessage: 'Failed to grant admin consent',
            description: 'Error message when admin consent fails',
          }),
        });
      }
    } catch (error) {
      toast({
        status: 'danger',
        message:
          error instanceof Error
            ? error.message
            : intl.formatMessage({
                id: 'MicrosoftWordIntegrationCard.consentError',
                defaultMessage: 'Failed to grant admin consent',
                description: 'Error message when admin consent fails',
              }),
      });
    } finally {
      setIsConsentLoading(false);
    }
  };

  return {
    isConsentLoading,
    handleGrantAdminConsent,
  };
};
